import type { ReactNode } from 'react'
import { useCoachingStore } from '../../store'
import type { SortSpec } from '../_lib/sorting'

interface SortChoice {
  field: SortSpec['field']
  label: string
}

const SORT_CHOICES: readonly SortChoice[] = [
  { field: 'plans', label: 'Study plans' },
  { field: 'elo', label: 'ELO' },
  { field: 'extId', label: 'External ID' },
  { field: 'topic', label: 'Topic / subtopic' },
  { field: 'difficulty', label: 'Difficulty' },
  { field: 'source', label: 'Source' },
  { field: 'company', label: 'Company' },
  // The one sort that visibly reorders the Sources tree (LG-827): its rows
  // have no ELO or difficulty, so every other sort is a tie there.
  { field: 'title', label: 'Title' },
]

// Persisted rows now carry real difficulty and source; plans/company remain
// mock-only until they get data backing (LG-250).
const PERSISTED_SORT_FIELDS = new Set<SortSpec['field']>([
  'elo',
  'extId',
  'topic',
  'difficulty',
  'source',
  'title',
])

const inputClass =
  'rounded-md border border-(--cd-border-2) bg-(--cd-inset) px-2 py-1 text-[11px] text-(--cd-text) outline-none focus:border-(--cd-amber)'

export function SortPanel(): ReactNode {
  const { curriculum } = useCoachingStore()
  const { sorts, setSorts } = curriculum
  const sortChoices = curriculum.persisted
    ? SORT_CHOICES.filter((choice) => PERSISTED_SORT_FIELDS.has(choice.field))
    : SORT_CHOICES

  const addSort = (): void => {
    const next = sortChoices.find(
      (choice) => !sorts.some((sort) => sort.field === choice.field),
    )
    if (next) setSorts([...sorts, { field: next.field, dir: 'asc' }])
  }

  return (
    <div
      id="library-sort-panel"
      className="rounded-[10px] border border-(--cd-border-2) bg-(--cd-panel-2) px-3 py-2.5"
    >
      <div className="space-y-2">
        {sorts.map((sort, index) => {
          const supported = sortChoices.some(
            (choice) => choice.field === sort.field,
          )
          return (
            <div
              key={`${sort.field}-${index}`}
              className="flex flex-wrap items-center gap-1.5"
            >
              <span aria-hidden="true" className="text-(--cd-text-faint)">
                ⠿
              </span>
              <select
                aria-label={`Sort ${index + 1} field`}
                value={sort.field}
                onChange={(event) => {
                  const field = event.target.value as SortSpec['field']
                  if (
                    sorts.some(
                      (candidate, candidateIndex) =>
                        candidateIndex !== index && candidate.field === field,
                    )
                  ) {
                    return
                  }
                  setSorts(
                    sorts.map((current, currentIndex) =>
                      currentIndex === index ? { ...current, field } : current,
                    ),
                  )
                }}
                className={inputClass}
              >
                {!supported ? (
                  <option value={sort.field}>{sort.field}</option>
                ) : null}
                {sortChoices.map((choice) => {
                  const usedElsewhere = sorts.some(
                    (candidate, candidateIndex) =>
                      candidateIndex !== index &&
                      candidate.field === choice.field,
                  )
                  return (
                    <option
                      key={choice.field}
                      value={choice.field}
                      disabled={usedElsewhere}
                    >
                      {choice.label}
                    </option>
                  )
                })}
              </select>
              <select
                aria-label={`Sort ${index + 1} direction`}
                value={sort.dir}
                onChange={(event) =>
                  setSorts(
                    sorts.map((current, currentIndex) =>
                      currentIndex === index
                        ? {
                            ...current,
                            dir: event.target.value as SortSpec['dir'],
                          }
                        : current,
                    ),
                  )
                }
                className={inputClass}
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
              <button
                type="button"
                aria-label={`Remove sort ${index + 1}`}
                onClick={() =>
                  setSorts(
                    sorts.filter((_, currentIndex) => currentIndex !== index),
                  )
                }
                className="rounded px-1.5 py-1 text-[11px] text-(--cd-text-faint) hover:text-(--cd-red)"
              >
                ✕
              </button>
            </div>
          )
        })}
      </div>

      <div className="mt-3 flex gap-4 border-t border-(--cd-border) pt-2">
        <button
          type="button"
          disabled={sortChoices.every((choice) =>
            sorts.some((sort) => sort.field === choice.field),
          )}
          onClick={addSort}
          className="text-[11px] text-(--cd-text-muted) hover:text-(--cd-text) disabled:cursor-not-allowed disabled:opacity-40"
        >
          ＋ Add sort
        </button>
        <button
          type="button"
          disabled={sorts.length === 0}
          onClick={() => setSorts([])}
          className="text-[11px] text-(--cd-text-faint) hover:text-(--cd-red) disabled:cursor-not-allowed disabled:opacity-40"
        >
          🗑 Delete sort
        </button>
      </div>
    </div>
  )
}
